import React, { useEffect, useState } from 'react';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import ScienceBackground from '../../components/layout/ScienceBackground';
import { FaAngleRight, FaPlay, FaGraduationCap, FaLock, FaCalendarAlt } from 'react-icons/fa';
import { Link, useParams, useNavigate } from 'react-router-dom';
import API_BASE_URL from '../../config/api';

const CourseDetails = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(true);
  const user = JSON.parse(localStorage.getItem('user'));

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchDetails = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/get_course_details.php?id=${courseId}`);
        const result = await response.json();
        if (result.status === 'success') {
          setCourse(result.data.course);
          setMonths(result.data.months);
        } else {
          navigate('/courses');
        }
      } catch (error) {
        console.error("Error fetching course details:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [courseId, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col pt-24">
        <Navbar />
        <div className="flex-grow flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!course) return null;

  return (
    <div className="font-sans bg-slate-50 min-h-screen text-slate-800 overflow-x-hidden selection:bg-rose-500 selection:text-white relative flex flex-col pt-24">
      <ScienceBackground />
      <Navbar />

      <div className="w-full px-6 md:px-12 py-4 relative z-10 max-w-7xl mx-auto">
        <div className="text-sm font-bold text-slate-500 flex items-center gap-2 pb-4 flex-wrap">
          <Link to="/" className="hover:text-red-500 cursor-pointer transition-colors whitespace-nowrap bg-white px-3 py-1 rounded-full shadow-sm border border-slate-100">Home</Link> 
          <FaAngleRight size={12} className="text-slate-400"/> 
          <Link to="/courses" className="hover:text-red-500 cursor-pointer transition-colors whitespace-nowrap bg-white px-3 py-1 rounded-full shadow-sm border border-slate-100">All Courses</Link> 
          <FaAngleRight size={12} className="text-slate-400"/> 
          <span className="text-red-500 whitespace-nowrap bg-red-50 px-3 py-1 rounded-full border border-red-100">{course.title}</span>
        </div>
      </div>

      {/* Hero Enrollment Board */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 mb-12">
        <div className="bg-white/70 backdrop-blur-2xl border border-white rounded-[2.5rem] shadow-[0_8px_40px_rgb(0,0,0,0.06)] p-6 md:p-10 flex flex-col md:flex-row gap-10">
          
          {/* Left Graphic Cover */}
          <div className={`w-full md:w-1/3 lg:w-[360px] aspect-square lg:aspect-auto border border-red-900/10 ${course.bg_pattern || 'bg-gradient-to-br from-red-800 via-rose-900 to-slate-900'} relative rounded-[2rem] shadow-2xl overflow-hidden flex-shrink-0 group transform hover:-translate-y-2 transition-transform duration-500`}>
             <div className="absolute inset-0 bg-transparent opacity-20"></div>
             <div className="absolute inset-0 bg-gradient-to-t from-red-900/80 to-transparent"></div>
             
             <div className="h-full flex flex-col justify-center items-center text-center w-full p-6 relative py-12">
                <FaGraduationCap className="text-white/30 text-6xl mx-auto mb-4 group-hover:scale-110 transition-transform duration-500" />
                <h3 className="text-white font-sinhala font-black text-3xl drop-shadow-xl z-10 leading-snug tracking-wide">
                    {course.title}
                </h3>
             </div>
             <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-md border border-white/40 text-white font-bold px-4 py-1.5 rounded-full shadow-lg text-sm">{course.tag || 'New'}</div>
          </div>

          {/* Right Information Details */}
          <div className="flex-1 w-full pt-4 flex flex-col justify-center">
            
            <div className="inline-block px-4 py-1.5 rounded-full bg-red-100 text-red-600 font-bold text-sm tracking-wide mb-6 border border-red-200 shadow-sm w-fit">
              🔥 {course.tag || 'PREMIUM COURSE'}
            </div>
            
            <h2 className="text-3xl md:text-5xl font-black font-sinhala text-slate-800 mb-8 leading-tight drop-shadow-sm">
                {course.title}
            </h2>
            {course.description && (
              <p className="text-slate-500 font-medium mb-8 leading-relaxed font-sinhala">{course.description}</p>
            )}

            {/* Metric Blocks */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
              <div className="bg-gradient-to-br from-slate-50 to-white p-5 rounded-2xl shadow-sm border border-slate-100 transform hover:scale-105 transition-transform duration-300">
                <div className="text-xs font-black tracking-widest text-slate-400 uppercase mb-2">PRICE</div>
                <div className="font-extrabold text-2xl text-slate-800">LKR {course.price}</div>
              </div>
              <div className="bg-gradient-to-br from-slate-50 to-white p-5 rounded-2xl shadow-sm border border-slate-100 transform hover:scale-105 transition-transform duration-300">
                <div className="text-xs font-black tracking-widest text-slate-400 uppercase mb-2">START DATE</div>
                <div className="font-extrabold text-xl text-slate-800 mt-1">{course.start_date || 'Immediate Access'}</div>
              </div>
              <div className="bg-gradient-to-br from-slate-50 to-white p-5 rounded-2xl shadow-sm border border-slate-100 transform hover:scale-105 transition-transform duration-300">
                <div className="text-xs font-black tracking-widest text-slate-400 uppercase mb-2">DURATION</div>
                <div className="font-extrabold text-lg text-slate-800 leading-tight">{course.duration || 'Self-Paced'}</div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 mt-auto">
              <button onClick={() => navigate(`/student/course-months/${course.id}`)} className="px-10 py-5 bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white font-black rounded-xl shadow-xl shadow-red-500/30 transition-all active:scale-95 text-lg flex items-center justify-center gap-3">
                <FaPlay /> Buy Course Now
              </button>
            </div>
          </div>

        </div>
      </div>

      {/* Months Section */}
      <div className="relative z-10 w-full max-w-5xl mx-auto px-6 md:px-12 pb-24 flex flex-col gap-10">
        <div className="flex flex-col gap-6">
          <div className="flex items-center gap-4 border-b-2 border-slate-200 pb-4">
            <div className="bg-red-500 w-2 h-8 rounded-full"></div>
            <h3 className="text-2xl font-black text-slate-800 uppercase tracking-widest">Course Months</h3>
          </div>

          {!user && (
            <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 text-amber-700 font-bold text-sm px-5 py-4 rounded-2xl">
              <FaLock />
              <span>Login and enroll to access course materials. Months are shown for preview only.</span>
            </div>
          )}

          {months.length === 0 ? (
            <div className="bg-white p-12 rounded-2xl border border-slate-100 text-center text-slate-400 font-bold">
              No months have been added to this course yet.
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {months.map((month, i) => (
                <div
                  key={month.id}
                  onClick={() => navigate(`/student/course-months/${course.id}`)}
                  className="flex items-center justify-between px-6 py-5 bg-white border border-slate-100 shadow-[0_2px_10px_rgb(0,0,0,0.03)] rounded-2xl hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-all cursor-pointer group"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-red-50 text-red-400 flex items-center justify-center font-black text-sm">
                      {String(i + 1).padStart(2, '0')}
                    </div>
                    <span className="font-bold text-slate-700 text-[15px] tracking-wide group-hover:text-red-600 transition-colors">
                      {month.title}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    {!user ? (
                      <span className="flex items-center gap-1.5 text-xs font-black text-slate-400 bg-slate-50 px-3 py-1 rounded-full border border-slate-100">
                        <FaLock size={10} /> Locked
                      </span>
                    ) : (
                      <span className="flex items-center gap-1.5 text-xs font-black text-orange-500 bg-orange-50 px-3 py-1 rounded-full border border-orange-100">
                        <FaCalendarAlt size={10} /> Enroll
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CourseDetails;
